import type {AgentModelData, AppArtifactBuilderData, AppArtifactOpenData, ChatService, ChatServiceParams, CrosstabsClassificationData, CrosstabsObjectivesData, CrosstabsOptionsData, CrosstabsFileRequestData, CrosstabsPlanData, CrosstabsProgressData, DiscoveryQuestionData, IntentResult, LocationRequestData, MarkdownArtifactPanelData, McpAppData, McpAppInputData, McpToolResultData, MicroAppOpenData, SiteBuilderCodeChunkData, SiteBuilderOptionsData, SiteBuilderPlanData, SkillLoadedData, SkillSelectionData, ProgressStepData, ProgressStepUpdateData, AuthoringIterationData, AuthoringVerifyData, LogData, WebsiteChangeSetReadyData} from '../components/R3Chat/types'
import {parsePublishedWebsiteEvent} from './publishedWebsite'

/** Callbacks consumed by the SSE parser — the streaming subset of ChatServiceParams. */
type StreamCallbacks = Omit<ChatServiceParams, 'message' | 'sessionId' | 'department' | 'history' | 'attachedFileIds' | 'attachments' | 'websiteEditContext' | 'websiteTargetReferences'>

/** Params for reattaching to an in-progress turn (GET /api/r3/chat/stream). */
export interface ResumeStreamParams extends StreamCallbacks {
  sessionId: string
  /** Replay events with seq > after. 0 (default) replays the whole turn. */
  after?: number
}

/**
 * WebChatService
 *
 * Implements ChatService for the web app using fetch + ReadableStream
 * to consume the SSE stream from POST /api/r3/chat.
 *
 * resumeStream() reattaches to an in-progress (or just-finished) turn via
 * GET /api/r3/chat/stream — the server replays the turn's durable event log
 * from an offset, then continues live. Both paths share one SSE parser, so
 * replayed events re-fire the exact callbacks that built the UI live.
 */
export class WebChatService implements ChatService {
  private baseUrl: string
  private accessToken: string | undefined
  private abortController: AbortController | null = null

  constructor(baseUrl: string, accessToken?: string) {
    this.baseUrl = baseUrl
    this.accessToken = accessToken
  }

  /** Update the access token (e.g. after Cognito refresh) */
  setAccessToken(token: string): void {
    this.accessToken = token
  }

  /** Cancel the current in-flight request */
  cancel(): void {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }

  private buildHeaders(json: boolean): Record<string, string> {
    const headers: Record<string, string> = json ? {'Content-Type': 'application/json'} : {}
    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`
    }
    // CL-34245: send the browser's IANA timezone so Ros can ground "today"/"now" in the
    // user's local time (the get_current_datetime tool + the always-on date line). Best-effort: any
    // failure just omits the header and the backend falls back to UTC.
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
      if (tz) headers['x-user-timezone'] = tz
    } catch { /* Intl unavailable: backend falls back to UTC */ }
    return headers
  }

  async sendMessage(params: ChatServiceParams): Promise<void> {
    const {message, sessionId, department, history, attachedFileIds, attachments, websiteEditContext, websiteTargetReferences, onError} = params

    // Create a new AbortController for this request
    this.abortController = new AbortController()
    const {signal} = this.abortController

    let response: Response

    try {
      response = await fetch(`${this.baseUrl}/api/r3/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: this.buildHeaders(true),
        signal,
        body: JSON.stringify({
          message,
          sessionId,
          department,
          conversationHistory: history,
          ...(attachedFileIds && attachedFileIds.length > 0 ? {attachedFileIds} : {}),
          ...(attachments && attachments.length > 0 ? {attachments} : {}),
          ...(websiteEditContext ? {websiteEditContext} : {}),
          ...(websiteTargetReferences && websiteTargetReferences.length > 0 ? {websiteTargetReferences} : {}),
        })
      })
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        // User cancelled — not an error
        return
      }
      onError(new Error(`Network error: ${(err as Error).message}`))
      return
    }

    await this.consumeSseResponse(response, params, signal, sessionId ?? '')
  }

  /**
   * Reattach to an in-progress turn: server replays the durable event log from `after`,
   * then live-tails until the turn completes. Same parser and callbacks as sendMessage,
   * so panels, progress cards, and streaming text all restore through existing handlers.
   */
  async resumeStream(params: ResumeStreamParams): Promise<void> {
    const {sessionId, after = 0, onError} = params

    this.abortController = new AbortController()
    const {signal} = this.abortController

    let response: Response
    try {
      const qs = new URLSearchParams({sessionId, after: String(after)})
      response = await fetch(`${this.baseUrl}/api/r3/chat/stream?${qs}`, {
        method: 'GET',
        credentials: 'include',
        headers: this.buildHeaders(false),
        signal,
      })
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        return
      }
      onError(new Error(`Network error: ${(err as Error).message}`))
      return
    }

    await this.consumeSseResponse(response, params, signal, sessionId)
  }

  /** Shared SSE consumption: parse events and dispatch to callbacks. */
  private async consumeSseResponse(
    response: Response,
    callbacks: StreamCallbacks,
    signal: AbortSignal,
    initialSessionId: string,
  ): Promise<void> {
    const {onChunk, onIntent, onSiteBuilderOptions, onSiteBuilderPlan, onSiteBuilderGenerating, onSiteBuilderCodeChunk, onSiteBuilderPreview, onSiteBuilderPublished, onWebsiteChangeSetReady, onMarkdownArtifactPanel, onSkillLoaded, onAppArtifactBuilder, onAppArtifactOpen, onSkillSelection, onDiscoveryQuestion, onCrosstabsProgress, onCrosstabsClassification, onCrosstabsObjectives, onCrosstabsPlan, onCrosstabsOptions, onCrosstabsFileRequest, onMcpApp, onMcpAppInput, onMcpToolResult, onMicroAppOpen, onLocationRequest, onAuthoringStep, onAuthoringStepUpdate, onAuthoringIteration, onAuthoringVerify, onLog, onHandoff, onAgentModel, onComplete, onError} = callbacks

    if (!response.ok) {
      onError(new Error(`Server error: ${response.status} ${response.statusText}`))
      return
    }

    if (!response.body) {
      onError(new Error('No response body'))
      return
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let returnedSessionId = initialSessionId

    try {
      while (true) {
        let readResult: ReadableStreamReadResult<Uint8Array>
        try {
          readResult = await reader.read()
        } catch (err) {
          if ((err as Error).name === 'AbortError' || signal.aborted) {
            // User cancelled — stop reading, not an error
            break
          }
          throw err
        }
        const {done, value} = readResult
        if (done) break

        buffer += decoder.decode(value, {stream: true})

        // SSE events are separated by double newlines
        const events = buffer.split('\n\n')
        buffer = events.pop() ?? ''

        for (const rawEvent of events) {
          if (!rawEvent.trim()) continue

          const lines = rawEvent.split('\n')
          let eventType = ''
          let dataStr = ''

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              eventType = line.slice(7).trim()
            } else if (line.startsWith('data: ')) {
              dataStr = line.slice(6).trim()
            }
            // `id: <seq>` lines are emitted on replayed/live-tail events; the parser
            // tolerates them (a future reconnect-mid-read enhancement can track them).
          }

          if (!dataStr) continue

          let data: any
          try {
            data = JSON.parse(dataStr)
          } catch {
            continue
          }

          switch (eventType) {
            case 'chunk':
              if (data.text) onChunk(data.text)
              break

            case 'intent':
              onIntent(data as IntentResult)
              break

            case 'complete':
              returnedSessionId = data.sessionId || returnedSessionId
              onComplete(returnedSessionId, data.tokenUsage, {
                contextTokens: data.contextTokens ?? 0,
                contextWindow: data.contextWindow ?? 200000,
                compacted: data.compacted ?? false
              })
              break

            case 'site_builder_options':
              if (onSiteBuilderOptions) onSiteBuilderOptions(data as SiteBuilderOptionsData)
              break

            case 'site_builder_plan':
              if (onSiteBuilderPlan) onSiteBuilderPlan(data as SiteBuilderPlanData)
              break

            case 'site_builder_generating':
              if (onSiteBuilderGenerating) onSiteBuilderGenerating(data.siteId)
              break

            case 'site_builder_code_chunk':
              if (onSiteBuilderCodeChunk) onSiteBuilderCodeChunk(data as SiteBuilderCodeChunkData)
              break

            case 'site_builder_preview':
              if (onSiteBuilderPreview) onSiteBuilderPreview(data.renderHtml ?? data.html, data.siteId, data.revision, data.html)
              break

            case 'website_changeset_ready':
              onWebsiteChangeSetReady?.(data as WebsiteChangeSetReadyData)
              break

            case 'markdown_artifact_panel':
              onMarkdownArtifactPanel?.(data as MarkdownArtifactPanelData)
              break

            case 'site_builder_published':
              if (onSiteBuilderPublished) {
                const published = parsePublishedWebsiteEvent(data)
                if (published) onSiteBuilderPublished(published.liveUrl, published.siteId)
              }
              break

            case 'skill_loaded':
              if (onSkillLoaded) onSkillLoaded(data as SkillLoadedData)
              break

            case 'app_artifact_builder':
              if (onAppArtifactBuilder) onAppArtifactBuilder(data as AppArtifactBuilderData)
              break

            case 'app_artifact_open':
              if (onAppArtifactOpen) onAppArtifactOpen(data as AppArtifactOpenData)
              break

            case 'skill_selection':
              if (onSkillSelection) onSkillSelection(data as SkillSelectionData)
              break

            case 'discovery_question':
              if (onDiscoveryQuestion) onDiscoveryQuestion(data as DiscoveryQuestionData)
              break

            case 'crosstabs_progress':
              if (onCrosstabsProgress) onCrosstabsProgress(data as CrosstabsProgressData)
              break

            case 'crosstabs_classification':
              if (onCrosstabsClassification) onCrosstabsClassification(data as CrosstabsClassificationData)
              break

            case 'crosstabs_objectives':
              if (onCrosstabsObjectives) onCrosstabsObjectives(data as CrosstabsObjectivesData)
              break

            case 'crosstabs_plan':
              if (onCrosstabsPlan) onCrosstabsPlan(data as CrosstabsPlanData)
              break

            case 'crosstabs_options':
              if (onCrosstabsOptions) onCrosstabsOptions(data as CrosstabsOptionsData)
              break

            case 'crosstabs_file_request':
              if (onCrosstabsFileRequest) onCrosstabsFileRequest(data as CrosstabsFileRequestData)
              break

            case 'mcp_app':
              if (onMcpApp) onMcpApp(data as McpAppData)
              break

            case 'mcp_app_input':
              if (onMcpAppInput) onMcpAppInput(data as McpAppInputData)
              break

            case 'mcp_tool_result':
              if (onMcpToolResult) onMcpToolResult(data as McpToolResultData)
              break

            case 'micro_app_open':
              if (onMicroAppOpen) onMicroAppOpen(data as MicroAppOpenData)
              break

            case 'location_request':
              if (onLocationRequest) onLocationRequest(data as LocationRequestData)
              break

            // ── Authoring pipeline events (CF backend) ──────────────
            case 'authoring_step':
              if (onAuthoringStep) onAuthoringStep(data as ProgressStepData)
              break

            case 'authoring_step_update':
              if (onAuthoringStepUpdate) onAuthoringStepUpdate(data as ProgressStepUpdateData)
              break

            case 'authoring_iteration':
              if (onAuthoringIteration) onAuthoringIteration(data as AuthoringIterationData)
              break

            case 'authoring_verify':
              if (onAuthoringVerify) onAuthoringVerify(data as AuthoringVerifyData)
              break

            case 'log':
              if (onLog) onLog(data as LogData)
              break

            case 'handoff':
              if (onHandoff) onHandoff(data as { from: string; to: string; reason: string; summary: string })
              break

            case 'agent_model':
              if (onAgentModel) onAgentModel(data as AgentModelData)
              break

            // ping (reattach heartbeat) and reattach_none fall through — no UI effect.

            case 'error':
              onError(new Error(data.message || 'Unknown server error'))
              return
          }
        }
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError' || signal.aborted) {
        // User cancelled — not an error
        return
      }
      onError(new Error(`Stream read error: ${(err as Error).message}`))
    } finally {
      reader.releaseLock()
      this.abortController = null
    }
  }
}
